/**
 * Marketing Operations & AI Research Specialist - job application (Philippines).
 * Client-safe: no DB access. Content transcribed from HR's "TELI Marketing
 * Application Form" (October 2026): 13 sections, conditional questions,
 * "None" exclusive, "Other" with details, uploads and one recorded answer.
 *
 * Rendered by MarketingApplicationWizard and validated by the public
 * step-save route through intl-form-engine.ts. Questions shared with the agent
 * form keep its columns (storage 'column'); the rest goes to extra_answers.
 *
 * Not in this form by HR's decision: available days and hours, time zone,
 * part-time / full-time and weekly hours available, and the typing test.
 */
import {
  FormCondition,
  FormFieldDef,
  FormFileSlot,
  FormStepItem,
  FormValues,
  conditionHolds,
  isFileSlotRequired,
  stepFields,
  stepFileSlots,
} from '@/lib/recruitment/intl-form-engine';
import {
  BACKUP_INTERNET_NONE,
  BACKUP_INTERNET_OPTIONS,
  BACKUP_INTERNET_OTHER,
  BACKUP_POWER_EQUIPMENT_OPTIONS,
  BACKUP_POWER_NONE,
  BACKUP_POWER_OPTIONS,
  BACKUP_POWER_OTHER,
  BACKUP_POWER_SOURCES,
  COMPUTER_TYPE_LABELS,
  COMPUTER_TYPE_OPTIONS,
  DEVICE_LABELS,
  DEVICE_OPTIONS,
  STORAGE_TYPE_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_UNKNOWN_OPTIONS,
  backupPowerEquipmentField,
  backupPowerRuntimeField,
} from '@/lib/recruitment/equipment-backup-options';

export const MARKETING_STEPS = [
  'personal',
  'identity',
  'work',
  'english',
  'audio',
  'ai_tools',
  'research',
  'organization',
  'skills',
  'equipment',
  'expectations',
  'consent',
  'review',
] as const;

export type MarketingStepKey = (typeof MARKETING_STEPS)[number];

export const MARKETING_STEP_TITLES: Record<MarketingStepKey, string> = {
  personal: 'Personal Information',
  identity: 'Photograph & Identity Verification',
  work: 'Work Experience',
  english: 'English Proficiency',
  audio: 'Audio Assessment',
  ai_tools: 'AI Tools & Digital Testing',
  research: 'Research & Problem Solving',
  organization: 'Organization & Remote Work',
  skills: 'Preferred Skills',
  equipment: 'Home Office Equipment & Internet',
  expectations: 'Start Date & Compensation Expectations',
  consent: 'Consent & Declaration',
  review: 'Review & Confirmation',
};

export const MARKETING_STEP_TITLES_ES: Record<MarketingStepKey, string> = {
  personal: 'Información personal',
  identity: 'Fotografía y verificación de identidad',
  work: 'Experiencia laboral',
  english: 'Dominio del inglés',
  audio: 'Evaluación de audio',
  ai_tools: 'Herramientas de IA y pruebas digitales',
  research: 'Investigación y solución de problemas',
  organization: 'Organización y trabajo remoto',
  skills: 'Habilidades preferidas',
  equipment: 'Equipo de home office e internet',
  expectations: 'Fecha de inicio y expectativas de compensación',
  consent: 'Consentimiento y declaración',
  review: 'Revisión y confirmación',
};

/** Steps saved through the step-save route (answers, plus their uploads). */
export const MARKETING_DATA_STEPS: MarketingStepKey[] = [
  'personal',
  'identity',
  'work',
  'english',
  'ai_tools',
  'research',
  'organization',
  'skills',
  'equipment',
  'expectations',
];

// ---------------------------------------------------------------------------
// Uploads and audio
// ---------------------------------------------------------------------------

export const MARKETING_FILE_KINDS = [
  'resume',
  'photo',
  'gov_id',
  'selfie_with_id',
  'evidence',
  'specs',
  'specs_backup',
  'speedtest',
] as const;
export type MarketingFileKind = (typeof MARKETING_FILE_KINDS)[number];

/** Every upload kind of every vacancy (recruitment_intl_files.kind). */
export const INTL_ALL_FILE_KINDS = [
  'gov_id',
  'selfie_with_id',
  'speedtest',
  'resume',
  'photo',
  'specs',
  'specs_backup',
  'evidence',
] as const;
export type IntlAnyFileKind = (typeof INTL_ALL_FILE_KINDS)[number];

export const INTL_ALL_FILE_LABELS: Record<IntlAnyFileKind, string> = {
  gov_id: 'Government-Issued ID',
  selfie_with_id: 'Selfie Holding Your Government ID',
  speedtest: 'Speedtest Screenshot',
  resume: 'Resume / CV',
  photo: 'Recent Photograph',
  specs: 'Computer Specifications Screenshot',
  specs_backup: 'Backup Device Specifications Screenshot',
  evidence: 'Supporting Evidence File',
};

export const INTL_ALL_FILE_LABELS_ES: Record<IntlAnyFileKind, string> = {
  gov_id: 'Identificación oficial',
  selfie_with_id: 'Selfie con su identificación oficial',
  speedtest: 'Captura de la prueba de velocidad',
  resume: 'Currículum / CV',
  photo: 'Fotografía reciente',
  specs: 'Captura de las especificaciones de la computadora',
  specs_backup: 'Captura de las especificaciones del equipo de respaldo',
  evidence: 'Archivo de evidencia',
};

export const MARKETING_AUDIO_PART = {
  key: 'work_example',
  title: 'Recorded answer',
  instructions:
    '"Describe a digital tool or workflow you tested, the issue or opportunity you identified, and how you communicated the next steps. If you do not have a work example, explain how you would approach this type of task."',
  scriptText: null,
  minSeconds: 20,
  maxSeconds: 120,
} as const;

export const MARKETING_AUDIO_PART_KEYS = [MARKETING_AUDIO_PART.key] as const;

/** Every audio part of every vacancy (recruitment_intl_audio.part). */
export const INTL_ALL_AUDIO_PART_KEYS = [
  'script_reading',
  'objection_handling',
  'self_introduction',
  'work_example',
] as const;

export const INTL_ALL_AUDIO_PART_LABELS: Record<string, string> = {
  script_reading: 'Part 1 - Script Reading',
  objection_handling: 'Part 2 - Customer Objection',
  self_introduction: 'Part 3 - Self Introduction',
  work_example: 'Recorded answer - tool or workflow tested',
};

export const INTL_ALL_AUDIO_PART_LABELS_ES: Record<string, string> = {
  script_reading: 'Parte 1 - Lectura de guion',
  objection_handling: 'Parte 2 - Objeción del cliente',
  self_introduction: 'Parte 3 - Presentación personal',
  work_example: 'Respuesta grabada - herramienta o flujo probado',
};

export const MARKETING_AUDIO_INSTRUCTIONS = [
  'Record directly on this page. Maximum duration: 2 minutes.',
  'Speak in English, in your own words, without reading a prepared script or using voice modification tools.',
  'You can listen to your recording, record it again, and confirm it before continuing.',
  'If recording does not work on your device, your answers stay saved - use the audio file upload under the recorder instead.',
];

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------

const NONE = 'None';
const OTHER = 'Other';
const UNKNOWN = 'I do not know';

export const MARKETING_ENGLISH_LEVELS = [
  'Intermediate',
  'Upper intermediate',
  'Advanced',
  'Native or near-native',
] as const;

const COMPUTER_AVAILABLE: FormCondition = {
  field: 'computer_availability',
  in: ['Yes, my own', 'Yes, through another arrangement'],
};
const BOTH_COMPUTERS: FormCondition = {
  all: [COMPUTER_AVAILABLE, { field: 'computer_type', equals: 'Both' }],
};
const HAS_LAPTOP: FormCondition = {
  all: [COMPUTER_AVAILABLE, { field: 'computer_type', in: ['Laptop', 'Both'] }],
};
const HAS_BACKUP_INTERNET: FormCondition = {
  field: 'backup_internet_options',
  anyExcept: BACKUP_INTERNET_NONE,
};
const INTERNET_AVAILABLE: FormCondition = { field: 'has_internet', equals: 'Yes' };

/** The equipment the applicant's backup power keeps running and for how long, per source. */
const BACKUP_POWER_SOURCE_ITEMS: FormStepItem[] = BACKUP_POWER_SOURCES.flatMap((source) => [
  {
    name: backupPowerEquipmentField(source.key),
    storage: 'extra' as const,
    label: `${source.short}: which equipment can it keep running?`,
    type: 'choice' as const,
    required: true,
    options: BACKUP_POWER_EQUIPMENT_OPTIONS,
    showWhen: { field: 'backup_power_sources', includes: source.option },
  },
  {
    name: backupPowerRuntimeField(source.key),
    storage: 'extra' as const,
    label: `${source.short}: approximately how long can it keep that equipment running?`,
    type: 'duration' as const,
    required: true,
    showWhen: { field: 'backup_power_sources', includes: source.option },
  },
]);

/**
 * Equipment questions of the form before 2026-10-22, replaced by the backup
 * power / backup internet blocks. Not asked any more; the HR view still shows
 * the answers of the applications that gave them.
 */
export const MARKETING_LEGACY_FIELDS: FormFieldDef[] = [
  { name: 'has_backup_internet', storage: 'extra', label: 'Do you have backup internet?', type: 'bool' },
  { name: 'backup_internet_details', storage: 'extra', label: 'Describe the backup.', type: 'text', maxLen: 1000 },
  { name: 'has_backup_power', storage: 'extra', label: 'Do you have backup power?', type: 'bool' },
  { name: 'backup_power_details', storage: 'extra', label: 'Describe the equipment and estimated operating time.', type: 'text', maxLen: 1000 },
];
const COMPUTER_AND_INTERNET: FormCondition = { all: [COMPUTER_AVAILABLE, INTERNET_AVAILABLE] };
const COMPUTER_OR_INTERNET_MISSING: FormCondition = {
  any: [
    { field: 'computer_availability', equals: 'Not yet' },
    { field: 'has_internet', equals: 'Not yet' },
  ],
};

export const MARKETING_START_LABELS: Record<string, string> = {
  '1_week': 'Within 1 week',
  '2_weeks': 'Within 2 weeks',
  '30_days': 'Within 30 days',
  another_date: 'Another date',
};

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export const MARKETING_STEP_ITEMS: Record<string, FormStepItem[]> = {
  personal: [
    { name: 'full_name', storage: 'column', label: 'Full name as shown on your government-issued ID', type: 'string', required: true, maxLen: 255 },
    { name: 'email', storage: 'column', label: 'Email address', type: 'string', required: true, maxLen: 255, inputType: 'email' },
    { name: 'mobile_number', storage: 'column', label: 'Phone number, including country code', type: 'string', required: true, maxLen: 50, inputType: 'tel', placeholder: '+63 ...' },
    { name: 'whatsapp_number', storage: 'column', label: 'WhatsApp number', type: 'string', required: true, maxLen: 50, inputType: 'tel', placeholder: '+63 ...', help: 'Enter the number including country code.', copyFrom: { field: 'mobile_number', label: 'Same as my phone number' } },
    { name: 'city', storage: 'column', label: 'City', type: 'string', required: true, maxLen: 100 },
    { name: 'state_province', storage: 'column', label: 'State or province', type: 'string', required: true, maxLen: 100 },
    { name: 'country_choice', storage: 'extra', label: 'Country of residence', type: 'choice', required: true, options: ['Philippines', 'Another country'] },
    { name: 'country', storage: 'column', label: 'Which country?', type: 'string', required: true, maxLen: 100, showWhen: { field: 'country_choice', equals: 'Another country' } },
    { name: 'linkedin_url', storage: 'column', label: 'LinkedIn profile (Optional)', type: 'string', maxLen: 500, inputType: 'url' },
    { name: 'portfolio_url', storage: 'extra', label: 'Portfolio or work sample link (Optional)', type: 'string', maxLen: 500, inputType: 'url', help: 'Only share portfolio material you are authorized to disclose.' },
    { file: 'resume', label: 'Resume / CV', hint: 'PDF or Word document.', accept: 'document', required: true },
  ],
  identity: [
    { note: 'Upload all three files. Images must be clear, complete, and unfiltered.' },
    { file: 'photo', label: 'Recent front-facing photograph', hint: 'Your full face must be clearly visible. JPG or PNG.', accept: 'image', required: true, capture: 'user' },
    { name: 'gov_id_type', storage: 'extra', label: 'Type of valid government-issued ID', type: 'choice', required: true, options: ['Passport', "Driver's license", 'National ID', 'Another government-issued ID'] },
    { name: 'gov_id_type_other', storage: 'extra', label: 'Which government-issued ID?', type: 'string', required: true, maxLen: 150, showWhen: { field: 'gov_id_type', equals: 'Another government-issued ID' } },
    { file: 'gov_id', label: 'Valid government-issued ID', hint: 'A clear photograph or copy. JPG, PNG, or PDF.', accept: 'image_pdf', required: true },
    { file: 'selfie_with_id', label: 'Selfie holding your government-issued ID', hint: 'Your face and the document must be visible in the same photograph. JPG or PNG.', accept: 'image', required: true, capture: 'user' },
  ],
  work: [
    { name: 'experience_level', storage: 'extra', label: 'How much professional experience do you have in fields related to this position?', type: 'choice', required: true, options: ['No experience', 'Less than 1 year', '1-3 years', 'More than 3 years'] },
    { name: 'work_areas', storage: 'extra', label: 'Which areas have you worked in?', type: 'multi', required: true, help: 'Select all that apply.', options: ['Digital marketing', 'Marketing operations', 'Research', 'QA or testing', 'Customer experience', 'Automation', 'Another related field', NONE], exclusiveOption: NONE },
    { name: 'work_areas_other', storage: 'extra', label: 'Which other related field?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'work_areas', includes: 'Another related field' } },
    { name: 'work_highlight', storage: 'extra', label: 'Describe your most relevant responsibilities and one result you achieved. Include your role and your personal contribution.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
    { name: 'employment_history', storage: 'extra', label: 'Employment history', type: 'roles', required: true, help: 'Add each relevant role. You may add more roles.', showWhen: { field: 'experience_level', in: ['Less than 1 year', '1-3 years', 'More than 3 years'] } },
  ],
  english: [
    { name: 'english_level', storage: 'column', label: 'How would you rate your spoken English?', type: 'choice', required: true, options: MARKETING_ENGLISH_LEVELS },
    { name: 'written_english_level', storage: 'extra', label: 'How would you rate your written English?', type: 'choice', required: true, options: MARKETING_ENGLISH_LEVELS },
    { name: 'english_independent_work', storage: 'extra', label: 'Can you independently write reports, explain findings, and participate in work discussions in English?', type: 'choice', required: true, options: ['Yes', 'Partially', 'No'] },
    { name: 'has_english_experience', storage: 'column', label: 'Have you worked in an English-speaking environment?', type: 'bool', required: true },
    { name: 'english_experience_details', storage: 'extra', label: 'State the duration and the types of communication you used in English.', type: 'text', required: true, maxLen: 1500, showWhen: { field: 'has_english_experience', equals: true } },
    { name: 'written_assessment', storage: 'extra', label: 'Written assessment', type: 'text', required: true, maxLen: 2000, rows: 8, words: { min: 100, max: 150 }, help: '"A signup form displays a success message, but the user does not receive the confirmation email. Write a short report explaining what you observed, what you would check next, and what remains unverified." Write 100-150 words in English, independently and without AI assistance.' },
  ],
  ai_tools: [
    { name: 'ai_tools_used', storage: 'extra', label: 'Which AI or automation tools have you used?', type: 'multi', required: true, help: 'Select all that apply.', options: ['ChatGPT', 'Claude', 'Another chatbot or AI tool', 'Automation platform', NONE], exclusiveOption: NONE },
    { name: 'ai_tools_other', storage: 'extra', label: 'Which other tools or platforms?', type: 'string', required: true, maxLen: 300, showWhen: { any: [{ field: 'ai_tools_used', includes: 'Another chatbot or AI tool' }, { field: 'ai_tools_used', includes: 'Automation platform' }] } },
    { name: 'ai_task_description', storage: 'extra', label: 'Describe a task you completed using AI. What did you do, and how did you verify the output? If you have no experience, state this.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
    { name: 'tested_experiences', storage: 'extra', label: 'Which digital experiences have you tested?', type: 'multi', required: true, help: 'Select all that apply.', options: ['Websites', 'Forms', 'Landing pages', 'Chatbots or AI agents', 'Signup or onboarding', 'Checkout', 'Tracking events', 'Automations', OTHER, NONE], exclusiveOption: NONE },
    { name: 'tested_experiences_other', storage: 'extra', label: 'Which other digital experiences?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'tested_experiences', includes: OTHER } },
    { name: 'test_description', storage: 'extra', label: 'Describe a test you performed. What did you find, and how did you document it? If you have no experience, state this.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
  ],
  research: [
    { note: 'If the example is confidential, describe your method without sharing sensitive information. A portfolio upload is not required.' },
    { name: 'competitor_analysis', storage: 'extra', label: 'Describe a competitor analysis or comparison you created: what you compared, which sources you used, and your conclusion. If you have no example, state this.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
    { name: 'evidence_link', storage: 'extra', label: 'Example or supporting evidence link (Optional)', type: 'string', maxLen: 500, inputType: 'url' },
    { file: 'evidence', label: 'Example or supporting evidence file (Optional)', hint: 'PDF or image.', accept: 'pdf_image' },
    { name: 'signup_dropoff', storage: 'extra', label: 'How would you investigate a signup flow where users appear to drop off before completion? Explain your first checks, the evidence you would collect, and your priorities.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
  ],
  organization: [
    { name: 'org_tools', storage: 'extra', label: 'Which tools do you use to organize your work?', type: 'multi', required: true, help: 'Select all that apply.', options: ['Asana', 'Google Sheets', 'Notion', 'Another project management or spreadsheet tool', NONE], exclusiveOption: NONE },
    { name: 'org_tools_other', storage: 'extra', label: 'Which other tool?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'org_tools', includes: 'Another project management or spreadsheet tool' } },
    { name: 'tracking_example', storage: 'extra', label: 'How do you track owners, deadlines, progress, and blockers? Share an example.', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
    { name: 'limited_instructions', storage: 'extra', label: 'Describe a situation where you received limited instructions. How did you clarify the objective and move the task forward?', type: 'text', required: true, maxLen: 3000, rows: 5, help: 'Written response in English.' },
    { name: 'has_remote_experience', storage: 'column', label: 'Have you worked remotely before?', type: 'bool', required: true },
    { name: 'remote_experience_details', storage: 'extra', label: 'For how long, and what were your main responsibilities?', type: 'text', required: true, maxLen: 1500, showWhen: { field: 'has_remote_experience', equals: true } },
  ],
  skills: [
    { note: 'These are preferred skills, not individually mandatory. Select "None" if you do not have this experience.' },
    { name: 'ad_platforms', storage: 'extra', label: 'Which advertising platforms have you worked with?', type: 'multi', required: true, help: 'Select all that apply.', options: ['Meta Ads', 'TikTok Ads', 'Google Ads', OTHER, NONE], exclusiveOption: NONE },
    { name: 'ad_platforms_other', storage: 'extra', label: 'Which other advertising platforms?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'ad_platforms', includes: OTHER } },
    { name: 'ad_activities', storage: 'extra', label: 'Describe the activities you performed directly.', type: 'text', required: true, maxLen: 2000, showWhen: { field: 'ad_platforms', anyExcept: NONE } },
    { name: 'tech_topics', storage: 'extra', label: 'Which technical topics have you used in practice?', type: 'multi', required: true, help: 'Select all that apply.', options: ['Analytics', 'Pixels', 'Events', 'Attribution', 'APIs', 'Webhooks', 'HTML', 'No-code', OTHER, NONE], exclusiveOption: NONE },
    { name: 'tech_topics_other', storage: 'extra', label: 'Which other technical topics?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'tech_topics', includes: OTHER } },
    { name: 'tech_experience', storage: 'extra', label: 'Briefly describe your hands-on experience.', type: 'text', required: true, maxLen: 2000, showWhen: { field: 'tech_topics', anyExcept: NONE } },
    { name: 'sectors', storage: 'extra', label: 'Have you worked in any of these sectors?', type: 'multi', required: true, help: 'Select all that apply.', options: ['SaaS', 'Artificial intelligence', 'Home services', 'Startups', OTHER, NONE], exclusiveOption: NONE },
    { name: 'sectors_other', storage: 'extra', label: 'Which other sectors?', type: 'string', required: true, maxLen: 300, showWhen: { field: 'sectors', includes: OTHER } },
  ],
  // Computer equipment and backup resources per HR's 2026-10-22 update: the
  // columns the IT rules read describe the (primary) computer; a second
  // "backup_*" block appears when the applicant will use both a desktop and a
  // laptop. Backup power and backup internet replace the former yes / no
  // questions (their answers stay readable in MARKETING_LEGACY_FIELDS).
  equipment: [
    { name: 'computer_availability', storage: 'extra', label: 'Do you have a computer available for work?', type: 'choice', required: true, options: ['Yes, my own', 'Yes, through another arrangement', 'Not yet'] },
    { name: 'computer_arrangement', storage: 'extra', label: 'Explain the availability arrangement.', type: 'text', required: true, maxLen: 1000, showWhen: { field: 'computer_availability', equals: 'Yes, through another arrangement' } },
    { name: 'computer_type', storage: 'extra', label: 'What type of computer will you use to work with TELI?', type: 'choice', required: true, options: COMPUTER_TYPE_OPTIONS, optionLabels: COMPUTER_TYPE_LABELS, showWhen: COMPUTER_AVAILABLE, help: 'Select one option. If you select Both, you will describe each computer separately.' },
    { name: 'primary_device', storage: 'extra', label: 'Which computer will be your primary device?', type: 'choice', required: true, options: DEVICE_OPTIONS, optionLabels: DEVICE_LABELS, showWhen: BOTH_COMPUTERS, help: 'The other computer will be your backup device.' },
    { note: 'Specifications of your primary computer', heading: true, showWhen: BOTH_COMPUTERS },
    { name: 'computer_brand_model', storage: 'extra', label: 'Brand and model', type: 'string', required: true, maxLen: 200, unknownOption: UNKNOWN, showWhen: COMPUTER_AVAILABLE },
    { name: 'computer_os', storage: 'column', label: 'Operating system', type: 'choice', required: true, options: ['Windows', 'macOS', OTHER, UNKNOWN], showWhen: COMPUTER_AVAILABLE },
    { name: 'os_version', storage: 'extra', label: 'Operating system version (if known)', type: 'string', maxLen: 100, placeholder: 'e.g. Windows 11', showWhen: COMPUTER_AVAILABLE },
    { name: 'computer_processor', storage: 'column', label: 'Processor: full model and generation, if known', type: 'string', required: true, maxLen: 150, placeholder: 'e.g. Intel Core i5-1135G7, AMD Ryzen 5 5500U, Apple M1', unknownOption: UNKNOWN, showWhen: COMPUTER_AVAILABLE },
    { name: 'computer_ram', storage: 'column', label: 'Installed RAM', type: 'choice', required: true, options: ['4 GB', '8 GB', '16 GB', '32 GB or more', OTHER, UNKNOWN], showWhen: COMPUTER_AVAILABLE },
    { name: 'computer_ram_other', storage: 'extra', label: 'How much RAM?', type: 'string', required: true, maxLen: 50, showWhen: { field: 'computer_ram', equals: OTHER } },
    { name: 'storage_type', storage: 'extra', label: 'Storage type', type: 'choice', required: true, options: STORAGE_TYPE_OPTIONS, showWhen: COMPUTER_AVAILABLE },
    { name: 'free_storage_gb', storage: 'extra', label: 'Available free storage in GB', type: 'number', required: true, min: 0, max: 100000, showWhen: COMPUTER_AVAILABLE },
    { file: 'specs', label: 'Screenshot of the computer specifications: OS, processor, and RAM', hint: 'JPG, PNG, or PDF. Hide unrelated personal information.', accept: 'image_pdf', required: COMPUTER_AVAILABLE },
    { note: 'Specifications of your backup device', heading: true, showWhen: BOTH_COMPUTERS },
    { name: 'backup_computer_brand_model', storage: 'extra', label: 'Brand and model', type: 'string', required: true, maxLen: 200, unknownOption: UNKNOWN, showWhen: BOTH_COMPUTERS },
    { name: 'backup_computer_os', storage: 'extra', label: 'Operating system', type: 'choice', required: true, options: ['Windows', 'macOS', OTHER, UNKNOWN], showWhen: BOTH_COMPUTERS },
    { name: 'backup_os_version', storage: 'extra', label: 'Operating system version (if known)', type: 'string', maxLen: 100, placeholder: 'e.g. Windows 11', showWhen: BOTH_COMPUTERS },
    { name: 'backup_computer_processor', storage: 'extra', label: 'Processor: full model and generation, if known', type: 'string', required: true, maxLen: 150, placeholder: 'e.g. Intel Core i5-1135G7, AMD Ryzen 5 5500U, Apple M1', unknownOption: UNKNOWN, showWhen: BOTH_COMPUTERS },
    { name: 'backup_computer_ram', storage: 'extra', label: 'Installed RAM', type: 'choice', required: true, options: ['4 GB', '8 GB', '16 GB', '32 GB or more', OTHER, UNKNOWN], showWhen: BOTH_COMPUTERS },
    { name: 'backup_computer_ram_other', storage: 'extra', label: 'How much RAM?', type: 'string', required: true, maxLen: 50, showWhen: { all: [BOTH_COMPUTERS, { field: 'backup_computer_ram', equals: OTHER }] } },
    { name: 'backup_storage_type', storage: 'extra', label: 'Storage type', type: 'choice', required: true, options: STORAGE_TYPE_OPTIONS, showWhen: BOTH_COMPUTERS },
    { name: 'backup_free_storage_gb', storage: 'extra', label: 'Available free storage in GB', type: 'number', required: true, min: 0, max: 100000, showWhen: BOTH_COMPUTERS },
    { file: 'specs_backup', label: 'Screenshot of the backup device specifications: OS, processor, and RAM', hint: 'JPG, PNG, or PDF. Hide unrelated personal information.', accept: 'image_pdf', required: BOTH_COMPUTERS },
    { name: 'laptop_unplugged', storage: 'extra', label: 'Does your laptop work without being plugged into a power outlet?', type: 'bool', required: true, showWhen: HAS_LAPTOP },
    { name: 'laptop_battery_duration', storage: 'extra', label: 'Approximately how long does its battery last during normal work use?', type: 'duration', required: true, showWhen: { all: [HAS_LAPTOP, { field: 'laptop_unplugged', equals: true }] } },
    { name: 'has_webcam', storage: 'column', label: 'Do you have a functioning webcam?', type: 'bool', required: true },
    { name: 'has_usb_headset', storage: 'column', label: 'Do you have a functioning headset with microphone?', type: 'bool', required: true },
    { name: 'headset_connection', storage: 'extra', label: 'Headset connection type', type: 'choice', required: true, options: ['USB', '3.5 mm', 'Bluetooth', OTHER], showWhen: { field: 'has_usb_headset', equals: true } },
    { name: 'headset_brand_model', storage: 'extra', label: 'Headset brand and model (Optional)', type: 'string', maxLen: 200, showWhen: { field: 'has_usb_headset', equals: true } },
    { name: 'has_internet', storage: 'extra', label: 'Do you have internet available at your planned work location?', type: 'choice', required: true, options: ['Yes', 'Not yet'] },
    { name: 'isp_provider', storage: 'column', label: 'Internet service provider', type: 'string', required: true, maxLen: 150, showWhen: INTERNET_AVAILABLE },
    { name: 'download_mbps', storage: 'column', label: 'Download speed in Mbps', type: 'number', required: true, min: 0, max: 10000, showWhen: INTERNET_AVAILABLE },
    { name: 'upload_mbps', storage: 'column', label: 'Upload speed in Mbps', type: 'number', required: true, min: 0, max: 10000, showWhen: INTERNET_AVAILABLE },
    { name: 'ping_ms', storage: 'extra', label: 'Ping in milliseconds', type: 'number', required: true, min: 0, max: 100000, showWhen: INTERNET_AVAILABLE },
    { name: 'connection_type', storage: 'extra', label: 'Connection you will use', type: 'choice', required: true, options: ['Ethernet', 'Wi-Fi', OTHER], showWhen: INTERNET_AVAILABLE },
    { file: 'speedtest', label: 'Screenshot of a recent speed test', hint: 'JPG, PNG, or PDF. Use your planned work computer and connection.', accept: 'image_pdf', required: COMPUTER_AND_INTERNET },
    { name: 'speedtest_date', storage: 'extra', label: 'Speed test date', type: 'date', required: true, showWhen: { file: 'speedtest' } },
    { note: 'Backup power', heading: true },
    { name: 'backup_power_sources', storage: 'extra', label: 'Do you have a backup power source that allows you to continue working during a power outage?', type: 'multi', required: true, options: BACKUP_POWER_OPTIONS, exclusiveOption: BACKUP_POWER_NONE, help: 'Select all that apply.' },
    { name: 'backup_power_other_system', storage: 'extra', label: 'Which other backup power system?', type: 'string', required: true, maxLen: 200, showWhen: { field: 'backup_power_sources', includes: BACKUP_POWER_OTHER } },
    ...BACKUP_POWER_SOURCE_ITEMS,
    { note: 'Backup internet', heading: true },
    { name: 'backup_internet_options', storage: 'extra', label: 'Do you have a backup internet connection ready to use if your main connection fails?', type: 'multi', required: true, options: BACKUP_INTERNET_OPTIONS, exclusiveOption: BACKUP_INTERNET_NONE, help: 'Select all that apply.' },
    { name: 'backup_internet_other_option', storage: 'extra', label: 'Which other backup internet option?', type: 'string', required: true, maxLen: 200, showWhen: { field: 'backup_internet_options', includes: BACKUP_INTERNET_OTHER } },
    { name: 'backup_internet_provider', storage: 'extra', label: 'Backup internet provider name', type: 'string', required: true, maxLen: 150, showWhen: HAS_BACKUP_INTERNET },
    { name: 'backup_internet_different_provider', storage: 'extra', label: 'Does it use a different provider or network from your main internet connection?', type: 'choice', required: true, options: YES_NO_UNKNOWN_OPTIONS, showWhen: HAS_BACKUP_INTERNET },
    { name: 'backup_internet_active_plan', storage: 'extra', label: 'Does the service currently have an active plan or available credit/data so it can be used immediately?', type: 'choice', required: true, options: YES_NO_OPTIONS, showWhen: HAS_BACKUP_INTERNET },
    { name: 'backup_internet_prepaid_plan', storage: 'extra', label: 'If it is prepaid, how do you make sure credit or data is available when an interruption occurs?', type: 'text', maxLen: 1000, showWhen: HAS_BACKUP_INTERNET, help: 'Short written explanation. Leave it empty if the service is not prepaid.' },
    { name: 'has_quiet_workspace', storage: 'column', label: 'Do you have a quiet, private workspace?', type: 'bool', required: true },
    { name: 'workspace_noise_level', storage: 'column', label: 'Typical noise level', type: 'choice', required: true, options: ['Low', 'Moderate', 'High'] },
    { name: 'can_do_it_verification', storage: 'extra', label: 'Can you participate in a technical verification session with TELI IT?', type: 'bool', required: true },
    { name: 'pending_improvements', storage: 'extra', label: 'Describe pending equipment or connectivity improvements and their expected availability date.', type: 'text', maxLen: 1500, requiredWhen: COMPUTER_OR_INTERNET_MISSING, help: 'Required if your computer or internet is not available yet; optional otherwise.' },
  ],
  expectations: [
    { name: 'start_availability', storage: 'column', label: 'When can you start?', type: 'choice', required: true, options: ['1_week', '2_weeks', '30_days', 'another_date'], optionLabels: MARKETING_START_LABELS },
    { name: 'start_date', storage: 'extra', label: 'Start date', type: 'date', required: true, showWhen: { field: 'start_availability', equals: 'another_date' } },
    { name: 'expected_compensation', storage: 'extra', label: 'Expected compensation', type: 'number', required: true, min: 0, max: 100000000 },
    { name: 'compensation_currency', storage: 'extra', label: 'Currency', type: 'choice', required: true, options: ['PHP', 'USD'] },
    { name: 'payment_basis', storage: 'extra', label: 'Payment basis', type: 'choice', required: true, options: ['Hourly', 'Weekly', 'Monthly'] },
    { name: 'compensation_weekly_hours', storage: 'extra', label: 'Weekly hours assumed in your compensation expectation', type: 'number', required: true, min: 1, max: 168 },
    { name: 'contractor_willingness', storage: 'extra', label: 'Are you willing to work as an independent contractor?', type: 'choice', required: true, options: ['Yes', 'No', 'I would like more information'] },
  ],
};

/** Every question of the form, in order (HR modal, review step, bootstrap). */
export const MARKETING_ALL_FIELDS: FormFieldDef[] = MARKETING_DATA_STEPS.flatMap((step) =>
  stepFields(MARKETING_STEP_ITEMS[step]),
);

export const MARKETING_ALL_FILE_SLOTS: FormFileSlot[] = MARKETING_DATA_STEPS.flatMap((step) =>
  stepFileSlots(MARKETING_STEP_ITEMS[step]),
);

/** Step each upload kind belongs to. */
export const MARKETING_FILE_STEP: Record<string, MarketingStepKey> = Object.fromEntries(
  MARKETING_DATA_STEPS.flatMap((step) =>
    stepFileSlots(MARKETING_STEP_ITEMS[step]).map((slot) => [slot.file, step] as const),
  ),
);

// ---------------------------------------------------------------------------
// Columns derived from the answers (so the shared inbox / IT rules read them)
// ---------------------------------------------------------------------------

/**
 * Columns the step-save route sets on top of the answers:
 *   personal     - country = Philippines unless another country was typed
 *   equipment    - owns_computer (IT rule "own PC"): only "Yes, my own"
 *   expectations - accepts_independent_contractor: Yes / No, NULL for
 *                  "I would like more information"
 */
export function marketingDerivedColumns(
  step: string,
  values: FormValues,
): Record<string, string | boolean | null> {
  if (step === 'personal') {
    return values.country_choice === 'Philippines' ? { country: 'Philippines' } : {};
  }
  if (step === 'equipment') {
    const availability = values.computer_availability;
    return {
      owns_computer: availability == null ? null : availability === 'Yes, my own',
    };
  }
  if (step === 'expectations') {
    const answer = values.contractor_willingness;
    return {
      accepts_independent_contractor: answer === 'Yes' ? true : answer === 'No' ? false : null,
    };
  }
  return {};
}

// ---------------------------------------------------------------------------
// Consent
// ---------------------------------------------------------------------------

export const MARKETING_CONSENT_ITEMS = [
  {
    key: 'consent_data_processing',
    text: 'I authorize TELI to collect, process, and store the information and documents provided, including my photograph, government-issued ID, and selfie, for recruitment purposes in accordance with the privacy notice.',
  },
  {
    key: 'consent_recorded_interviews',
    text: 'I understand that the procedure will be explained and my consent requested before any interview is recorded.',
  },
  {
    key: 'consent_accuracy',
    text: 'I confirm that the information provided is accurate and that the examples submitted reflect my own contribution.',
  },
] as const;

/**
 * Link to the approved recruitment privacy notice, shown before the consent
 * boxes. Empty until HR provides the approved notice (no link is shown).
 */
export const MARKETING_PRIVACY_NOTICE_URL = '';

// ---------------------------------------------------------------------------
// Final-submit check
// ---------------------------------------------------------------------------

/**
 * What still blocks the final submit: unfinished sections, the recording and
 * the uploads the answers make necessary. Read from the stored application
 * (never from the browser).
 */
export function marketingMissingForSubmit(
  input: {
    completedSteps: ReadonlySet<string>;
    values: FormValues;
    uploadedKinds: ReadonlySet<string>;
    audioParts: ReadonlySet<string>;
  },
  /** The words in force (Recruitment -> Job Applications); code defaults otherwise. */
  wording: {
    stepTitles: Record<string, string>;
    stepItems: Record<string, FormStepItem[]>;
  } = { stepTitles: MARKETING_STEP_TITLES, stepItems: MARKETING_STEP_ITEMS },
): string[] {
  const missing: string[] = [];
  for (const step of MARKETING_DATA_STEPS) {
    if (!input.completedSteps.has(step)) {
      missing.push(wording.stepTitles[step] ?? MARKETING_STEP_TITLES[step]);
    }
  }
  const slots = MARKETING_DATA_STEPS.flatMap((step) =>
    stepFileSlots(wording.stepItems[step] ?? MARKETING_STEP_ITEMS[step]),
  );
  for (const slot of slots) {
    if (
      isFileSlotRequired(slot, input.values, input.uploadedKinds) &&
      !input.uploadedKinds.has(slot.file)
    ) {
      missing.push(slot.label);
    }
  }
  for (const part of MARKETING_AUDIO_PART_KEYS) {
    if (!input.audioParts.has(part)) {
      missing.push(wording.stepTitles.audio ?? MARKETING_STEP_TITLES.audio);
    }
  }
  return missing;
}

/** A computer is available to the applicant (own or through another arrangement). */
export function marketingComputerAvailable(values: FormValues): boolean {
  return conditionHolds(COMPUTER_AVAILABLE, values, new Set());
}
